import { takeLatest } from 'redux-saga';
import { put, call, fork, select } from 'redux-saga/effects';
import { fromJS } from 'immutable';

import { createDeliverable, updateDeliverableInfo, getDeliverable } from '../../utils/jsonapi-functions/deliverables';
import { getDeliverableId, getAssignDeliverable } from '../../selectors/deliverable-assign-selectors';
import { updateDiscussionDeliverables } from '../../utils/jsonapi-functions/discussions';
import { getDiscussionId, getDiscussionName } from '../../selectors/discussion-selectors';
import { getCampaignId, getPermissionId } from '../../selectors/campaign-selectors';
import { MODAL_SET, MODAL_LOADING, MODAL_ERROR, MODAL_CLEAR } from '../../constants/modal';
import { reportError } from '../../utils/error-handling-service';
import { trackEvent } from '../../utils/metric-analytics-service';
import { DELIVERABLE_SET, DISCUSSION_PAGE } from '../../utils/static-configuration';
import { getMostRecentDocument } from '../../utils/helper-functions';
import { SEND_MESSAGE, UPDATE_DELIVERABLES } from '../../containers/discussion/constants';
import {
  NEW_DELIVERABLE,
  SHOW_EXISTING_DELIVERABLE,
  SHOW_EXISTING_DELIVERABLE_SUCCESS,
  EDIT_EXISTING_DELIVERABLE,
} from './constants';

export function* createNewDeliverable(action) {
  const deliverableData = action.deliverableData;
  const createDeliverableCallback = action.createDeliverableCallback;
  const discussionId = yield select(getDiscussionId);
  deliverableData.discussionId = discussionId;

  try {
    yield put({ type: MODAL_LOADING, loading: true });

    // Create a deliverable
    const newDeliverable = yield call(createDeliverable, deliverableData);

    // Update a discussion with the deliverable Id
    yield call(updateDiscussionDeliverables, discussionId, newDeliverable);

    const newDeliverableJSON = newDeliverable.toJSONTree();
    newDeliverableJSON.assetType = newDeliverableJSON.assetType.join(',');
    newDeliverableJSON.documents = {};
    const myPermissionId = yield select(getPermissionId);

    // Updates state with the new deliverable info
    yield put({
      type: UPDATE_DELIVERABLES,
      deliverableData: fromJS(newDeliverableJSON),
    });

    const message = newDeliverableJSON.title;
    const metaData = {
      messageType: 'deliverable',
      permissionId: myPermissionId,
      discussionId,
      deliverableId: newDeliverableJSON.id,
      deliverableStatus: 'added',
    };

    const campaignId = yield select(getCampaignId);
    const discussionName = yield select(getDiscussionName);

    const assignDeliverableData = {
      campaignId,
      collaborationId: discussionId,
      collaborationName: discussionName,
      deliverableId: newDeliverableJSON.id,
      deliverableName: newDeliverableJSON.title,
    };
    trackEvent('Assign Deliverable', assignDeliverableData);

    // Sends sendbird deliverable message
    const messageData = {
      message,
      metaData,
      page: DISCUSSION_PAGE,
    };
    yield put({ type: SEND_MESSAGE, data: messageData });

    yield call(createDeliverableCallback);
    yield put({ type: MODAL_CLEAR });
  } catch (error) {
    reportError('SET_NEW_DELIVERABLE_ERROR', error.message);
    yield put({ type: MODAL_ERROR, error });
  }
}

export function* showExistingDeliverable(action) {
  const { deliverable } = action.data;
  yield put({ type: SHOW_EXISTING_DELIVERABLE_SUCCESS, deliverable });

  yield put({ type: MODAL_SET, modalName: DELIVERABLE_SET });
}

function getUpdatedItems(stateDeliverable, newDeliverableData) {
  let updatedItems = [];
  if (stateDeliverable.get('title') !== newDeliverableData.title) updatedItems.push('title');
  if (stateDeliverable.get('channelType') !== newDeliverableData.channelType) updatedItems.push('channel type');
  if (stateDeliverable.get('assetType') !== newDeliverableData.assetType[0]) updatedItems.push('asset type');
  if (stateDeliverable.get('dueDate') !== newDeliverableData.dueDate) updatedItems.push('due date');
  if (stateDeliverable.get('liveDateEnd') !== newDeliverableData.liveDateEnd) updatedItems.push('live date');
  if (newDeliverableData.description && stateDeliverable.get('description') !== newDeliverableData.description) updatedItems.push('requirements');
  return updatedItems;
}

function createUpdateMessage(updatedItems) {
  let updatedMessage = '';
  const numberOfChanges = updatedItems.length;
  for (let i = 0; i < numberOfChanges; i++) {
    const item = updatedItems[i];
    let adjustedItem = i < numberOfChanges - 2 ? `${item}, ` : `${item} `;
    if (numberOfChanges > 1 && i + 1 === numberOfChanges) adjustedItem = `and ${adjustedItem}`;
    updatedMessage += adjustedItem;
  }
  return `The ${updatedMessage}${numberOfChanges > 1 ? 'were' : 'was'} updated.`;
}

export function* editExistingDeliverable(action) {
  try {
    yield put({ type: MODAL_LOADING, loading: true });

    const { deliverableData } = action.data;
    const deliverableId = yield select(getDeliverableId);
    const deliverable = yield select(getAssignDeliverable);

    const updatedItems = getUpdatedItems(deliverable, deliverableData);

    if (updatedItems.length) {
      const updateMessage = createUpdateMessage(updatedItems);

      yield call(updateDeliverableInfo, deliverableId, deliverableData);
      const updatedDeliverableJSON = yield call(getDeliverable, deliverableId);
      updatedDeliverableJSON.assetType = typeof updatedDeliverableJSON.assetType === 'string'
        ? updatedDeliverableJSON.assetType
        : updatedDeliverableJSON.assetType.join(',');
      updatedDeliverableJSON.mostRecentDocument = getMostRecentDocument(updatedDeliverableJSON);

      // Updates state with the updated deliverable info
      yield put({ type: UPDATE_DELIVERABLES, deliverableData: fromJS(updatedDeliverableJSON) });

      const discussionId = yield select(getDiscussionId);
      const myPermissionId = yield select(getPermissionId);

      const message = updatedDeliverableJSON.title;
      const metaData = {
        messageType: 'deliverable',
        permissionId: myPermissionId,
        discussionId,
        deliverableId: updatedDeliverableJSON.id,
        deliverableStatus: 'updated',
        updateMessage,
      };
      if (updatedDeliverableJSON.mostRecentDocument) metaData.docId = updatedDeliverableJSON.mostRecentDocument.id;

      // Sends sendbird deliverable message
      const messageData = {
        message,
        metaData,
        page: DISCUSSION_PAGE,
      };
      yield put({ type: SEND_MESSAGE, data: messageData });
    }

    yield put({ type: MODAL_CLEAR });
  } catch (error) {
    reportError('UPDATE_DELIVERABLE_ERROR', error.message);
    yield put({ type: MODAL_ERROR, error });
  }
}

export function* watchEditExistingDeliverable() {
  yield takeLatest(EDIT_EXISTING_DELIVERABLE, editExistingDeliverable);
}

export function* watchShowExistingDeliverable() {
  yield takeLatest(SHOW_EXISTING_DELIVERABLE, showExistingDeliverable);
}

export function* watchCreateDeliverable() {
  yield takeLatest(NEW_DELIVERABLE, createNewDeliverable);
}

export default function* deliverableSaga() {
  yield [
    fork(watchCreateDeliverable),
    fork(watchShowExistingDeliverable),
    fork(watchEditExistingDeliverable),
  ];
}



/** WEBPACK FOOTER **
 ** ./src/containers/deliverable-assign-modal/sagas.js
 **/